/**
 * Local-mode statement snapshot from ticker+'f'+'v3' (ADR-0034).
 * Additive stream — does not draw from the golden v3 scalars.
 */
import type {
  BalancePeriod,
  CashFlowPeriod,
  IncomePeriod,
  StockFinancials,
  StockRatios,
} from '@meridian/contracts';
import { STATEMENT_YEARS_MAX } from '@meridian/contracts';

import { fnv1a } from '../rand/fnv1a';
import { createRng } from '../rand/mulberry32';
import { seedFinancials, seedQuoteVolume } from '../rand/seed';

import type { Rng } from '../rand/types';

const INCOME_YEARS = [2023, 2024, 2025, 2026] as const;

export function syntheticAvgVolume(ticker: string, sharesOut: number): number | null {
  const rng = createRng(fnv1a(seedQuoteVolume(ticker)));
  const turnover = 0.002 + rng() * 0.018;
  const mean = sharesOut * turnover;
  /* Null is "not computable", not "zero volume" — 0 is a real halted session (R2b). */
  return Number.isFinite(mean) && mean > 0 ? mean : null;
}

function unit(rng: Rng, lo: number, span: number): number {
  return lo + rng() * span;
}

interface Margins {
  readonly gross: number;
  readonly operating: number;
  readonly profit: number;
}

/* Drawn first and returned as plain numbers so `buildIncome` can use the SAME
   profit margin the ratios report, without a `?? fallback` for a null that the
   `StockRatios` type admits but this function never produces. */
function buildMargins(rng: Rng): Margins {
  const gross = unit(rng, 0.35, 0.5);
  const operating = gross * unit(rng, 0.55, 0.35);
  return { gross, operating, profit: operating * unit(rng, 0.7, 0.25) };
}

function buildRatios(rng: Rng, pe: number, margins: Margins): StockRatios {
  const { gross, operating, profit } = margins;
  /*
   * Both draws happen unconditionally and are nulled afterwards. Drawing only
   * when `pe > 0` would make the draw count before `roe` depend on the P/E, so
   * a ticker's roe/pb/de would all shift if its P/E crossed zero.
   */
  const epsDraw = unit(rng, 2, 12);
  const peFwdDraw = pe * unit(rng, 0.7, 0.25);
  return {
    epsTtm: pe > 0 ? epsDraw : null,
    peFwd: pe > 0 ? peFwdDraw : null,
    grossMargin: gross,
    operatingMargin: operating,
    profitMargin: profit,
    roe: unit(rng, 0.08, 0.9),
    roa: unit(rng, 0.03, 0.45),
    pb: unit(rng, 1.5, 20),
    de: unit(rng, 0.2, 8),
    currentRatio: unit(rng, 0.8, 3.5),
    dividendRate: unit(rng, 0, 2.5),
    payoutRatio: unit(rng, 0, 0.55),
    totalCash: unit(rng, 5e9, 8e10),
    totalDebt: unit(rng, 2e9, 5e10),
    freeCashFlow: unit(rng, 1e9, 4e10),
    operatingCashFlow: unit(rng, 5e9, 1.2e11),
  };
}

function buildIncome(rng: Rng, revBillions: number, profitMargin: number): readonly IncomePeriod[] {
  /* Trim the OLDEST years, matching the API's `.slice(-STATEMENT_YEARS_MAX)`. */
  const years = INCOME_YEARS.slice(-STATEMENT_YEARS_MAX);
  const latest = Math.max(revBillions, 1) * 1e9;
  return years.map((year, index) => {
    const isLatest = index === years.length - 1;
    /* Drawn even for the latest year so the draw count stays fixed. */
    const jitter = 0.55 + rng() * 0.2;
    const frac = (index + 1) / years.length;
    /*
     * The newest bar IS the security's own revenue. Scaling it down by `jitter`
     * put a $217B bar directly beneath a "Revenue (TTM) $391B" grid cell.
     */
    const revenue = isLatest ? latest : latest * Math.pow(frac, 1.4) * jitter;
    /* Derived from the drawn margin, so the chart and the ratios agree. */
    const netIncome = revenue * profitMargin;
    return { year, revenue, netIncome };
  });
}

function scaleForYear(rng: Rng, index: number, count: number, isLatest: boolean): number {
  const jitter = 0.7 + rng() * 0.2;
  return isLatest ? 1 : ((index + 1) / count) * jitter;
}

function buildBalanceSheet(rng: Rng, income: readonly IncomePeriod[]): readonly BalancePeriod[] {
  const latestRevenue = income[income.length - 1]?.revenue ?? 1e9;
  return income.map((period, index) => {
    const isLatest = index === income.length - 1;
    const scale = scaleForYear(rng, index, income.length, isLatest);
    const assets = latestRevenue * (1.4 + rng() * 0.5) * scale;
    const liabilities = assets * (0.4 + rng() * 0.25);
    const cash = assets * (0.1 + rng() * 0.15);
    const debt = liabilities * (0.35 + rng() * 0.3);
    const currentAssets = assets * (0.35 + rng() * 0.2);
    const currentLiabilities = liabilities * (0.4 + rng() * 0.2);
    const retained = (period.netIncome ?? 0) * (2 + rng() * 3);
    const goodwill = assets * (0.02 + rng() * 0.06);
    const longTerm = debt * (0.6 + rng() * 0.3);
    return {
      year: period.year,
      totalAssets: assets,
      totalLiabilities: liabilities,
      equity: assets - liabilities,
      cash,
      totalDebt: debt,
      currentAssets,
      currentLiabilities,
      retainedEarnings: retained,
      goodwill,
      longTermDebt: longTerm,
    };
  });
}

function buildCashFlow(rng: Rng, income: readonly IncomePeriod[]): readonly CashFlowPeriod[] {
  return income.map((period, index) => {
    const isLatest = index === income.length - 1;
    const scale = scaleForYear(rng, index, income.length, isLatest);
    const operating = Math.max(period.netIncome ?? 0, 1e8) * (1.2 + rng() * 0.6) * scale;
    const capex = Math.abs(operating) * (0.08 + rng() * 0.12);
    const investing = -capex - Math.abs(operating) * (0.02 + rng() * 0.08);
    const buybacks = Math.abs(operating) * (0.4 + rng() * 0.35);
    const dividendsPaid = Math.abs(operating) * (0.08 + rng() * 0.12);
    const financing = -buybacks - dividendsPaid - Math.abs(operating) * (0.05 + rng() * 0.1);
    return {
      year: period.year,
      operating,
      investing,
      financing,
      freeCashFlow: operating - capex,
      capex,
      dividendsPaid,
      buybacks,
      debtRepaid: null,
      debtIssued: null,
      netChangeCash: operating + investing + financing,
    };
  });
}

export function syntheticFinancials(
  ticker: string,
  revBillions: number,
  pe: number,
): StockFinancials {
  const rng = createRng(fnv1a(seedFinancials(ticker)));
  const margins = buildMargins(rng);
  const ratios = buildRatios(rng, pe, margins);
  const income = buildIncome(rng, revBillions, margins.profit);
  /* ADR-0035 series MUST draw after income so existing ratio/income values stay.
     Ratio cash/debt/FCF/OCF draws still happen (stream lock); the statement
     years are internally consistent, then the ratio cells are aligned to the
     latest year so the grid cannot contradict the chart. */
  const balanceSheet = buildBalanceSheet(rng, income);
  const cashFlow = buildCashFlow(rng, income);
  const latestBalance = balanceSheet[balanceSheet.length - 1];
  const latestFlow = cashFlow[cashFlow.length - 1];
  return {
    ratios: {
      ...ratios,
      totalCash: latestBalance?.cash ?? ratios.totalCash,
      totalDebt: latestBalance?.totalDebt ?? ratios.totalDebt,
      operatingCashFlow: latestFlow?.operating ?? ratios.operatingCashFlow,
      freeCashFlow: latestFlow?.freeCashFlow ?? ratios.freeCashFlow,
    },
    income: [...income],
    balanceSheet: [...balanceSheet],
    cashFlow: [...cashFlow],
  };
}
